/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ILoyaltyTermsViewProps } from '@msdyn365-commerce-modules/account-management';
import { ILoyaltyTermsProps } from '../definition-extensions/loyalty-terms.ext.props.autogenerated';

import { AsyncResult, Customer } from '@msdyn365-commerce/retail-proxy';
import { TEMPORARY_ACCOUNT_NUMBER_LENGTH } from '../utils/constants';
import { trackVClubRegistration } from '../utilities/datalayer';

interface ILoyaltyTermsData {
    customerInformation: AsyncResult<Customer>;
}

const LoyaltyTermsView: React.FC<ILoyaltyTermsViewProps & ILoyaltyTermsProps<ILoyaltyTermsData>> = props => {
    const customerInformation = props.data.customerInformation.result;

    // The user does NOT have a customer number yet, they still have a tmp_guid
    let tempAccount = false;
    if (customerInformation?.AccountNumber?.length === TEMPORARY_ACCOUNT_NUMBER_LENGTH) {
        tempAccount = true;
    }

    const auth = props.context.request.user.isAuthenticated;

    // const hostname = props?.context?.request?.url?.requestUrl?.hostname || ''; // Protip: this does NOT keep the /de or whatever, so make sure to fix it if used in the future

    const { LoyaltyTerms, modalToggle, modal, loading } = props;

    React.useEffect(() => {
        const alreadySent = localStorage.getItem('alreadySentDataLayerEventForTempUser');

        if (!alreadySent && auth && tempAccount) {
            trackVClubRegistration();
            localStorage.setItem('alreadySentDataLayerEventForTempUser', 'true');
        }
    }, [auth, tempAccount]);

    if (auth && tempAccount) {
        return (
            <div className='loyalty-join'>
                {props.resources.joinedLoyaltyThankYouHeader && (
                    <h2 className='loyalty-join-header'>{props.resources.joinedLoyaltyThankYouHeader}</h2>
                )}
                {props.resources.joinedLoyaltyParagraphContent && (
                    <div className='loyalty-join-paragraph'>
                        <p>{props.resources.joinedLoyaltyParagraphContent}</p>
                    </div>
                )}
                {/* {props.resources.loyaltyBalanceCheckURL && props.resources.joinedLoyaltyBalanceCheckLinkText && (<a className='loyalty-join-balance-check-link' href={`https://${hostname}${props.resources.loyaltyBalanceCheckURL}`}>{props.resources.joinedLoyaltyBalanceCheckLinkText}</a>)} */}
            </div>
        );
        // User is NOT temp but IS auth --> old/synced user
    } else if (auth && !loading && modal && !tempAccount) {
        if (props.config.layout === 'wideLayout') {
            return (
                <Module {...LoyaltyTerms}>
                    {props.config.showImages && props.config.marketingImage ? (
                        <div className='row'>
                            <div className='col-sm-3 col-12'>
                                <img src={props.config.marketingImage.src} alt={props.config.marketingImage.altText} />
                            </div>
                            <div className='col-sm-6 col-12 with-images'>
                                {props.config.marketingText && <p>{props.config.marketingText}</p>}
                                {modalToggle}
                            </div>
                            <div className='col-sm-3 col-12 second-image'>
                                <img src={props.config.marketingImage.src} alt={props.config.marketingImage.altText} />
                            </div>
                        </div>
                    ) : (
                        <div className='without-images'>
                            {props.config.marketingText && <p className='marketing-text'>{props.config.marketingText}</p>}
                            {modalToggle}
                        </div>
                    )}
                    <Node {...modal.modal}>
                        <Node {...modal.modalHeader}>{modal.heading}</Node>
                        <Node {...modal.modalBody}>
                            {modal.terms}
                            <Node {...modal.agreeSection}>
                                {modal.checkbox}
                                {modal.agreeText}
                            </Node>
                        </Node>
                        <Node {...modal.modalFooter}>
                            {modal.submitButton}
                            {modal.cancelButton}
                        </Node>
                    </Node>
                </Module>
            );
        } else {
            return (
                <Module {...LoyaltyTerms}>
                    <div className='terms-card-layout'>
                        {props.config.showImages && props.config.marketingImage && (
                            <img src={props.config.marketingImage.src} alt={props.config.marketingImage.altText} />
                        )}
                        {props.config.marketingText && <p>{props.config.marketingText}</p>}
                        {modalToggle}
                    </div>
                    <Node {...modal.modal}>
                        <Node {...modal.modalHeader}>{modal.heading}</Node>
                        <Node {...modal.modalBody}>
                            {modal.terms}
                            <Node {...modal.agreeSection}>
                                {modal.checkbox}
                                {modal.agreeText}
                            </Node>
                        </Node>
                        <Node {...modal.modalFooter}>
                            {modal.submitButton}
                            {modal.cancelButton}
                        </Node>
                    </Node>
                </Module>
            );
        }
    } else {
        return null;
    }
};

export default LoyaltyTermsView;
